import React, { useRef, useState, useEffect } from 'react';
import './CustomImageCropper.css';

const CustomImageCropper = ({ imageSrc, onCrop, onCancel }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      // Force reset state after a brief delay to ensure proper rendering on all devices
      setTimeout(() => {
        if (canvasRef.current && imageRef.current) {
          const canvas = canvasRef.current;
          const img = imageRef.current;
          const cropRadius = Math.min(canvas.width, canvas.height) / 2 - 20;
          const minScale = (cropRadius * 2) / Math.min(img.width, img.height);
          setScale(minScale);
          setPosition({ x: 0, y: 0 });
        }
      }, 100);
    };
    img.onerror = (error) => {
      console.error('Error loading image:', error);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Draw canvas
  useEffect(() => {
    if (!imageLoaded || !imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    ctx.save();
    ctx.translate(canvas.width / 2 + position.x, canvas.height / 2 + position.y);
    ctx.scale(scale, scale);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    ctx.restore();

    // Draw circular crop overlay
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, Math.min(canvas.width, canvas.height) / 2 - 20, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw circle border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, Math.min(canvas.width, canvas.height) / 2 - 20, 0, Math.PI * 2);
    ctx.stroke();
  }, [imageLoaded, scale, position]);

  // Mouse/Touch handlers
  const handlePointerDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const point = getPointerPosition(e);
    setDragStart({ x: point.x - position.x, y: point.y - position.y });
  };

  // Constrain position so circle stays within image bounds
  const constrainPosition = (newPos) => {
    if (!imageRef.current || !canvasRef.current) return newPos;
    
    const canvas = canvasRef.current;
    const img = imageRef.current;
    const cropRadius = Math.min(canvas.width, canvas.height) / 2 - 20;
    
    // Calculate scaled image dimensions
    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;
    
    // Calculate maximum allowed offsets (how far from center the image can be)
    const maxOffsetX = Math.max(0, (scaledWidth - cropRadius * 2) / 2);
    const maxOffsetY = Math.max(0, (scaledHeight - cropRadius * 2) / 2);
    
    return {
      x: Math.max(-maxOffsetX, Math.min(maxOffsetX, newPos.x)),
      y: Math.max(-maxOffsetY, Math.min(maxOffsetY, newPos.y))
    };
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const point = getPointerPosition(e);
    const newPos = {
      x: point.x - dragStart.x,
      y: point.y - dragStart.y
    };
    setPosition(constrainPosition(newPos));
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  const getPointerPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  // Pinch zoom for mobile
  const touchDistanceRef = useRef(null);
  
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      touchDistanceRef.current = distance;
    } else if (e.touches.length === 1) {
      handlePointerDown(e);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      if (touchDistanceRef.current) {
        const scaleChange = distance / touchDistanceRef.current;
        const minScale = getMinScale();
        setScale(prev => {
          const newScale = Math.max(minScale, Math.min(5, prev * scaleChange));
          setTimeout(() => setPosition(p => constrainPosition(p)), 0);
          return newScale;
        });
        touchDistanceRef.current = distance;
      }
    } else if (e.touches.length === 1) {
      handlePointerMove(e);
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      touchDistanceRef.current = null;
    }
    if (e.touches.length === 0) {
      handlePointerUp();
    }
  };

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Get minimum scale (maximum zoomed out while keeping circle within image)
  const getMinScale = () => {
    if (!imageRef.current || !canvasRef.current) return 0.1;
    const canvas = canvasRef.current;
    const img = imageRef.current;
    const cropRadius = Math.min(canvas.width, canvas.height) / 2 - 20;
    // Scale so circle diameter fits within smallest image dimension
    return (cropRadius * 2) / Math.min(img.width, img.height);
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const minScale = getMinScale();
    setScale(prev => {
      const newScale = Math.max(minScale, Math.min(5, prev * delta));
      // Constrain position after zoom change
      setTimeout(() => setPosition(p => constrainPosition(p)), 0);
      return newScale;
    });
  };

  // Zoom buttons
  const handleZoomIn = () => {
    setScale(prev => {
      const newScale = Math.min(5, prev * 1.2);
      setTimeout(() => setPosition(p => constrainPosition(p)), 0);
      return newScale;
    });
  };

  const handleZoomOut = () => {
    const minScale = getMinScale();
    setScale(prev => {
      const newScale = Math.max(minScale, prev / 1.2);
      setTimeout(() => setPosition(p => constrainPosition(p)), 0);
      return newScale;
    });
  };

  const handleReset = () => {
    if (imageRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const img = imageRef.current;
      // Reset to maximum zoom out - use consistent calculation
      const minScale = getMinScale();
      setScale(minScale);
      setPosition({ x: 0, y: 0 });
    }
  };

  // Crop and return result
  const handleCrop = async () => {
    if (!imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const img = imageRef.current;
    
    // Create output canvas for cropped image
    const outputSize = 400;
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outputSize;
    outputCanvas.height = outputSize;
    const outputCtx = outputCanvas.getContext('2d');

    // Calculate crop area
    const cropRadius = Math.min(canvas.width, canvas.height) / 2 - 20;
    const scaleFactor = outputSize / (cropRadius * 2);

    // Draw cropped image
    outputCtx.save();
    outputCtx.beginPath();
    outputCtx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
    outputCtx.clip();

    outputCtx.translate(outputSize / 2, outputSize / 2);
    outputCtx.scale(scale * scaleFactor, scale * scaleFactor);
    outputCtx.translate(position.x / scale, position.y / scale);
    outputCtx.drawImage(img, -img.width / 2, -img.height / 2);
    outputCtx.restore();

    // Convert to blob
    outputCanvas.toBlob((blob) => {
      onCrop(blob);
    }, 'image/jpeg', 0.95);
  };

  if (!imageLoaded) {
    return (
      <div className="custom-cropper-loading">
        <div className="loading-spinner"></div>
        <p>Loading image...</p>
      </div>
    );
  }

  return (
    <div className="custom-image-cropper" ref={containerRef}>
      <div className="cropper-canvas-container">
        <canvas
          ref={canvasRef}
          width={400}
          height={400}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
          style={{ touchAction: 'none' }}
        />
        <div className="cropper-hint">
          <span className="desktop-hint">🖱️ Drag to move • Scroll to zoom</span>
          <span className="mobile-hint">👆 Drag to move • Pinch to zoom</span>
        </div>
      </div>

      <div className="cropper-controls">
        <div className="zoom-controls">
          <button onClick={handleZoomOut} className="zoom-btn">−</button>
          <span className="zoom-label">Zoom</span>
          <button onClick={handleZoomIn} className="zoom-btn">+</button>
        </div>
        <button onClick={handleReset} className="reset-btn">🔄 Reset</button>
      </div>

      <div className="cropper-actions">
        <button onClick={onCancel} className="cancel-btn">Cancel</button>
        <button onClick={handleCrop} className="apply-btn">Apply</button>
      </div>
    </div>
  );
};

export default CustomImageCropper;
